import { createLoadFromRatecon, revisionResultText, uploadErrorText, type OpenedRevision } from './uploadFlow'
import { ATTACHED_HEADER, DATA_NOTE } from './untrustedText'

export { ATTACHED_HEADER }

/** What "＋ Attach documents" offers (v17): PDF, PNG and JPG. */
export const ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg'

/** The largest file the composer takes (v17): 15 MB each. */
const ATTACH_MAX_BYTES = 15 * 1024 * 1024

/** The toast for a pick with a file of another type or over the limit (v17 copy). */
export const ATTACH_REFUSAL = 'Use PDF, PNG or JPG documents under 15 MB each.'

/** The note under the file list in the bubble: what our check does with an attached file (the board reads it as a rate con). */
export const ATTACHED_NOTE = 'Read as rate confirmations: load number, broker, rate and stops are taken from each document. Check them on the load.'

const ATTACHABLE_NAME = /\.(pdf|png|jpe?g)$/i

/** Why a pick is refused (the v17 toast), or null when every file may be attached. The whole pick is refused, as v17 does. */
export const attachRefusal = (files: readonly File[]): string | null =>
    files.some((file) => file.size > ATTACH_MAX_BYTES || !ATTACHABLE_NAME.test(file.name)) ? ATTACH_REFUSAL : null

/** What the uploads of one send did: a line per filed document, the revisions waiting for a choice, and the files that failed. */
interface AttachOutcome {
    lines: string[]
    revisions: OpenedRevision[]
    failed: Array<{ file: File; message: string }>
}

/**
 * Files each attached document through the board's own rate con upload (the composer's upload before v17, `createLoadFromRatecon`),
 * one at a time. A con the board filed as a revision of a load it had is listed as waiting for review, and returned for the chat to
 * show; a failed upload is returned with the board's words and adds no line.
 */
export async function uploadAttachments(files: readonly File[]): Promise<AttachOutcome> {
    const outcome: AttachOutcome = { lines: [], revisions: [], failed: [] }
    for (const file of files) {
        try {
            const upload = await createLoadFromRatecon(file)
            if (upload.kind === 'revision') {
                outcome.revisions.push(upload.revision)
                outcome.lines.push(revisionResultText(upload.revision, { kind: 'left' }))
            } else outcome.lines.push(upload.message)
        } catch (error) {
            outcome.failed.push({ file, message: uploadErrorText(error) })
        }
    }
    return outcome
}

/** The message a send with documents carries: the typed words, then the attached part (its header and one line per document). */
export const attachedMessage = (typed: string, lines: readonly string[]): string =>
    [typed.trim(), ...(lines.length ? [ATTACHED_HEADER, ...lines] : [])].filter(Boolean).join('\n')

const LABELLED_NAME = new RegExp(`File “([^”]*)”${DATA_NOTE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g')

/**
 * A user message split for its bubble: the typed words, and the names of the documents its attached part filed. A message with no
 * attached part (or one whose lines name no file) has no files, and its text is the whole message. The uploads' own lines are for
 * the agent and are not shown.
 */
export function attachedOf(content: string): { text: string; files: string[] } {
    const lines = content.split('\n')
    const at = lines.indexOf(ATTACHED_HEADER)
    if (at < 0) return { text: content, files: [] }
    const files = lines.slice(at + 1).flatMap((line) => [...line.matchAll(LABELLED_NAME)].map((match) => match[1]))
    if (!files.length) return { text: content, files: [] }
    return { text: lines.slice(0, at).join('\n'), files: [...new Set(files)] }
}
